import { useState, type FormEvent } from 'react';
import { fuelPricesApi, type FuelPrice } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { DataTable } from '@/components/DataTable';
import { adminNavItems } from '@/pages/navItems';
import { formatManilaLocalDateTime, parseManilaLocalDateTime } from '@dispatchdash/shared';
import { useToast } from '@/components/Feedback';
import { formatDateTime, formatPeso } from '@/lib/format';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';

export function AdminFuelPrices() {
  const { data: prices, loading, error, reload } = useAsync(() => fuelPricesApi.list(), []);
  const [pricePerLitre, setPricePerLitre] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(() => formatManilaLocalDateTime(new Date()));
  const toast = useToast();
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const { flash, ok: showOk, error: showError, clear: clearFlash } = useFlash();

  const rows = Array.isArray(prices) ? prices : [];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    clearFlash();
    try {
      await fuelPricesApi.create({
        pricePerLitre,
        effectiveFrom: parseManilaLocalDateTime(effectiveFrom).toISOString(),
        notes: notes || undefined,
      });
      setPricePerLitre('');
      setNotes('');
      showOk(`Fuel price ${formatPeso(pricePerLitre)} per litre saved`);
      toast('Fuel price saved');
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not save the fuel price.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header">
          <p className="kicker">Fleet</p>
          <h1>Fuel prices</h1>
          <p className="text-muted">
            Diesel pump prices over time — used for fuel surcharges and to spot unusual fuel use.
          </p>
        </div>

        <div className="fuel-layout">
          <form className="admin-form" onSubmit={handleSubmit}>
            <h2>Add pump price</h2>
            <Input
              label="Price per litre (₱)"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={pricePerLitre}
              onChange={(e) => setPricePerLitre(e.target.value)}
              required
              placeholder="65.50"
            />
            <Input
              label="Effective from (Manila time)"
              type="datetime-local"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              required
            />
            <Input
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Station or market note"
            />
            <FlashMessage flash={flash} />
            <Button type="submit" loading={saving}>Save price</Button>
          </form>

          <div className="fuel-table">
            {loading ? (
              <div className="loading-center"><div className="spinner" /></div>
            ) : (
              <DataTable<FuelPrice>
                data={rows}
                keyField="id"
                loadError={error}
                searchPlaceholder="Filter fuel prices…"
                emptyMessage="No fuel prices yet — add today’s pump price using the form."
                columns={[
                  {
                    key: 'price',
                    header: 'Price per litre',
                    value: (row) => Number(row.pricePerLitre),
                    render: (row) => (
                      <span className="data-num">{formatPeso(row.pricePerLitre)}</span>
                    ),
                  },
                  {
                    key: 'from',
                    header: 'Effective from',
                    value: (row) => row.effectiveFrom,
                    render: (row) => formatDateTime(row.effectiveFrom),
                  },
                  {
                    key: 'to',
                    header: 'Effective to',
                    value: (row) => row.effectiveTo ?? '',
                    render: (row) =>
                      row.effectiveTo
                        ? formatDateTime(row.effectiveTo)
                        : 'Current',
                  },
                  {
                    key: 'notes',
                    header: 'Notes',
                    value: (row) => row.notes ?? '',
                    render: (row) => row.notes || '—',
                  },
                ]}
              />
            )}
          </div>
        </div>

        <style>{`
          .fuel-layout {
            display: grid;
            grid-template-columns: 320px 1fr;
            gap: var(--space-xl);
            align-items: start;
          }
          @media (max-width: 900px) {
            .fuel-layout { grid-template-columns: 1fr; }
          }
          .admin-form {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-xl);
            display: flex;
            flex-direction: column;
            gap: var(--space-md);
          }
          .admin-form h2 {
            font-size: 1.125rem;
            margin-bottom: var(--space-xs);
          }
          .fuel-table {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-md);
            overflow: auto;
          }
          .data-num {
            font-family: var(--font-display);
            font-weight: 700;
            font-variant-numeric: tabular-nums;
          }
        `}</style>
      </div>
    </Layout>
  );
}
